import { useState, useEffect, useCallback } from 'react';

import { api } from '../lib/api/client';

interface CatalogInstance {
  id: string;
  name: string;
  catalog_name?: string;
  subdomain?: string;
  branding?: {
    tagline?: string;
    logo?: string;
    colors?: {
      primary?: string;
      secondary?: string;
    };
  };
}

// In-memory cache for catalog instances
const catalogCache = new Map<string, CatalogInstance>();

export function useCatalogInstance() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getCatalogInstance = useCallback(
    async (catalogId: string): Promise<CatalogInstance | null> => {
      if (!catalogId) return null;

      // Check cache first
      if (catalogCache.has(catalogId)) {
        return catalogCache.get(catalogId)!;
      }

      try {
        setLoading(true);
        setError(null);

        const response = await api.get(`/api/catalog-instances/${catalogId}`);
        const catalogInstance = response.catalogInstance;

        // Cache the result
        catalogCache.set(catalogId, catalogInstance);

        return catalogInstance;
      } catch (err: any) {
        console.error('Failed to fetch catalog instance:', err);
        setError(err.message || 'Failed to fetch catalog instance');

        // Return a fallback for the hardcoded main catalog UUID
        if (catalogId === 'aaaaaaaa-bbbb-1111-2222-aaaaaaaaaaaa') {
          const fallback: CatalogInstance = {
            id: catalogId,
            name: 'TechAdds Main Catalog',
          };
          catalogCache.set(catalogId, fallback);
          return fallback;
        }

        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const getCatalogDisplayName = useCallback(
    async (catalogId?: string): Promise<string> => {
      if (!catalogId) return 'Unknown Catalog';

      // Handle the hardcoded main catalog UUID
      if (catalogId === 'aaaaaaaa-bbbb-1111-2222-aaaaaaaaaaaa') {
        return 'Main Catalog';
      }

      const catalogInstance = await getCatalogInstance(catalogId);
      return catalogInstance?.name || 'Partner Catalog';
    },
    [getCatalogInstance],
  );

  const clearCache = useCallback(() => {
    catalogCache.clear();
  }, []);

  return {
    getCatalogInstance,
    getCatalogDisplayName,
    loading,
    error,
    clearCache,
  };
}
